import { useCallback, useEffect, useMemo, useState } from 'react';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import map from 'lodash/map';
import toLower from 'lodash/toLower';
import uniqBy from 'lodash/uniqBy';
import flatMap from 'lodash/flatMap';
import compact from 'lodash/compact';
import size from 'lodash/size';

import { ID } from '../../../../types';
import { UserFullName } from '../../../../main/users/usersTypes';
import { ProjectNanoID } from '../../../../main/projects/projectsTypes';
import {
  TeamNanoID,
  TeamTeamTypeEnum
} from '../../../../main/teams/teamsTypes';
import { TaskNanoID } from '../../../../main/tasks/tasksTypes';

import {
  FETCH_PROJECT_MEMBERS_QUERY,
  FetchProjectMembersQueryResponse
} from '../../../../main/projectMembers/queries/projectMembers.query';
import {
  FETCH_PROJECT_INVITED_MEMBERS_QUERY,
  FetchProjectInvitedMembersQueryResponse
} from '../../../../main/projectMemberInvites/queries/fetchProjectInvitedMembers.query';
import {
  FETCH_TASK_MEMBER_INVITES_QUERY,
  FetchTaskMemberInvitesQueryResponse
} from '../../../../main/taskMemberInvites/queries/fetchTaskMemberInvites.query';
import {
  FETCH_CACHE_TASK_MEMBERS_QUERY,
  CacheFetchTaskMembersQueryResponse
} from '../../../../main/taskMembers/queries/fetchCacheTaskMembers.query';
import {
  CACHE_FETCH_SHOW_PROJECTS_TASK_PARENT_PROJECTS_QUERY,
  CacheFetchShowProjectsTaskParentsQueryResponse
} from '../../../../main/projectsTasks/queries/fetchCacheShowProjectsTaskParents.query';
import {
  FETCH_CACHE_TASK_SMART_CONTRACTS_SHARES_QUERY,
  FetchCacheTaskSmartContractsSharesQueryResponse
} from '../../../../main/smartContracts/queries/fetchCacheTaskSmartContractsShares.query';
import {
  FETCH_TEAM_USERS_AND_CONTRACTORS_QUERY,
  FetchTeamUsersAndContractorsQueryResponse
} from '../../../../main/teams/queries/fetchTeamUsersAndContractors.query';

import { useCacheTaskMembers } from '../../../../main/taskMembers/hooks/useCacheTaskMembers';
import { useProjectContext } from '../../../../main/projects/hooks/useProjectContext';
import { useTaskContext } from '../../../../main/tasks/hooks/useTaskContext';
import { useProjectMembers } from '../../../../main/projectMembers/hooks/useProjectMembers';
import { useCacheTeam } from '../../../../main/teams/hooks/useCacheTeam';
import { usePaginatedTaskMemberInvites } from '../../../../main/taskMemberInvites/hooks/usePaginatedTaskMemberInvites';
import { usePaginatedProjectMemberInvites } from '../../../../main/projectMemberInvites/hooks/usePaginatedProjectMemberInvites';
import { useCachePaginatedSmartContracts } from '../../../../main/smartContracts/hooks/useCachePaginatedSmartContracts';
import { useCacheProjectsTask } from '../../../../main/projectsTasks/hooks/useCacheProjectsTask';

import { MentionsPlugin } from '../MentionsPlugin';
import { MentionTypeaheadOption } from '../../utils/MentionTypeaheadOption';

import { TeamCache } from '../../../../main/teams/TeamCache';
import { ProjectCache } from '../../../../main/projects/ProjectCache';
import { TaskCache } from '../../../../main/tasks/TaskCache';
import { SmartContractCache } from '../../../../main/smartContracts/SmartContractCache';

import { GeneralLedgerGeneralLedgerTypes } from '../../../../main/generalLedgers/generalLedgersTypes';

import { MentionsPluginAllValues } from './ProjectTeamMentionsPlugin.types';

import { ProjectsTaskCache } from '../../../../main/projectsTasks/ProjectsTaskCache';

interface ProjectTeamMentionsPluginProps {
  companyNanoId?: TeamNanoID;
  projectNanoId?: ProjectNanoID;
  taskNanoId?: TaskNanoID;
  initialMentionIds?: ID[];
}

interface ProjectTeamMentionsPluginUserOption {
  id: ID;
  fullName: string;
  image?: {
    file: string;
  };
  highlight?: boolean;
}

function ProjectTeamMentionsPlugin({
  companyNanoId: propsCompanyNanoId,
  projectNanoId: propsProjectNanoId,
  taskNanoId: propsTaskNanoId,
  initialMentionIds
}: ProjectTeamMentionsPluginProps) {
  const {
    projectNanoId: projectContextProjectNanoId,
    companyNanoId: projectContextCompanyNanoId
  } = useProjectContext();

  const {
    taskProjectNanoId: taskContextProjectNanoId,
    companyNanoId: taskContextCompanyNanoId,
    taskNanoId: taskNanoIdFromContext
  } = useTaskContext();

  const projectNanoId =
    propsProjectNanoId ||
    taskContextProjectNanoId ||
    projectContextProjectNanoId;

  const companyNanoId =
    propsCompanyNanoId ||
    taskContextCompanyNanoId ||
    projectContextCompanyNanoId;

  const taskNanoId = useMemo<TaskNanoID | undefined>(
    () => propsTaskNanoId || taskNanoIdFromContext,
    [propsTaskNanoId, taskNanoIdFromContext]
  );

  const { projectsTask } =
    useCacheProjectsTask<CacheFetchShowProjectsTaskParentsQueryResponse>({
      cacheKey: ProjectsTaskCache.showProjectsTaskBreadcrumbsCacheKey(),
      uuid: projectNanoId,
      query: CACHE_FETCH_SHOW_PROJECTS_TASK_PARENT_PROJECTS_QUERY
    });

  const {
    projectMembers: rootProjectMembers,
    filterProjectMembers,
    projectMembersFilters
  } = useProjectMembers<FetchProjectMembersQueryResponse>({
    cacheKey: ProjectCache.rootProjectMembersCacheKey(projectNanoId),
    query: FETCH_PROJECT_MEMBERS_QUERY,
    initialFilters: { projectNanoId: projectsTask?.rootProject?.nanoId },
    initialLimit: 1000,
    options: {
      enabled: !!projectsTask?.rootProject?.nanoId,
      enabledPlaceholder: !!projectsTask?.rootProject?.nanoId,
      withoutPrefetch: true
    }
  });

  useEffect(() => {
    if (
      projectsTask?.rootProject?.nanoId &&
      projectMembersFilters.projectNanoId !== projectsTask.rootProject.nanoId
    ) {
      filterProjectMembers({
        projectNanoId: projectsTask.rootProject.nanoId
      });
    }
  }, [projectsTask?.rootProject?.nanoId]);

  const { projectMembers } =
    useProjectMembers<FetchProjectMembersQueryResponse>({
      cacheKey: ProjectCache.projectMembersCacheKey(projectNanoId),
      query: FETCH_PROJECT_MEMBERS_QUERY,
      initialFilters: { projectNanoId },
      initialLimit: 1000,
      options: {
        enabled: !!projectNanoId,
        enabledPlaceholder: !!projectNanoId,
        withoutPrefetch: true
      }
    });

  const { projectMemberInvites } =
    usePaginatedProjectMemberInvites<FetchProjectInvitedMembersQueryResponse>({
      cacheKey: ProjectCache.projectInvitesMembersCacheKey(projectNanoId),
      query: FETCH_PROJECT_INVITED_MEMBERS_QUERY,
      initialFilters: { projectNanoId },
      options: {
        enabled: !!projectNanoId,
        enabledPlaceholder: !!projectNanoId,
        withoutPrefetch: true
      }
    });

  const { taskMembers } =
    useCacheTaskMembers<CacheFetchTaskMembersQueryResponse>({
      cacheKey: TaskCache.taskMembersCacheKey(taskNanoId),
      query: FETCH_CACHE_TASK_MEMBERS_QUERY,
      initialFilters: {
        taskNanoId: {
          eq: taskNanoId
        }
      },
      initialLimit: 1000,
      options: {
        enabled: !!taskNanoId,
        enabledPlaceholder: !!taskNanoId,
        withoutPrefetch: true
      }
    });

  const { smartContracts } =
    useCachePaginatedSmartContracts<FetchCacheTaskSmartContractsSharesQueryResponse>(
      {
        query: FETCH_CACHE_TASK_SMART_CONTRACTS_SHARES_QUERY,
        cacheKey: taskNanoId
          ? SmartContractCache.taskSmartContractsSharesCacheKey(taskNanoId)
          : SmartContractCache.projectSmartContractsSharesCacheKey(
              projectNanoId
            ),
        initialFilters: taskNanoId
          ? { taskNanoId: { eq: taskNanoId } }
          : { projectNanoId: { eq: projectNanoId } },
        options: {
          enabled: !!taskNanoId || !!projectNanoId,
          enabledPlaceholder: !!taskNanoId || !!projectNanoId,
          withoutPrefetch: true
        }
      }
    );

  const { taskMemberInvites } =
    usePaginatedTaskMemberInvites<FetchTaskMemberInvitesQueryResponse>({
      cacheKey: TaskCache.taskMembersInvitesCacheKey(taskNanoId),
      query: FETCH_TASK_MEMBER_INVITES_QUERY,
      initialFilters: { taskNanoId: taskNanoId },
      initialLimit: 1000,
      options: {
        enabled: !!taskNanoId,
        enabledPlaceholder: !!taskNanoId,
        withoutPrefetch: true
      }
    });

  const { team } = useCacheTeam<FetchTeamUsersAndContractorsQueryResponse>({
    uuid: companyNanoId,
    query: FETCH_TEAM_USERS_AND_CONTRACTORS_QUERY,
    cacheKey: TeamCache.teamUsersAndContractorsCacheKey(companyNanoId)
  });

  const users = useMemo<ProjectTeamMentionsPluginUserOption[]>(() => {
    const shares = filter(
      flatMap(
        smartContracts,
        (smartContract) => smartContract.smartContractShares
      ),
      (share) =>
        share.generalLedger.generalLedgerType ===
          GeneralLedgerGeneralLedgerTypes.DEFAULT &&
        share.generalLedger.company.companyType === TeamTeamTypeEnum.WORKERS
    );

    const teamUsers = team?.users || [];
    const teamContractors = team?.contractors || [];

    return uniqBy(
      filter(
        [
          ...map(rootProjectMembers, 'user'),
          ...map(projectMembers, 'user'),
          ...map(taskMembers, 'user'),
          ...teamUsers,
          ...teamContractors,
          ...map(projectMemberInvites, 'invitedUser'),
          ...map(taskMemberInvites, 'invitedUser'),
          ...map(shares, (share) => share.generalLedger.company.owner)
        ],
        (member) => member && !member.blocked
      ),
      'id'
    );
  }, [
    rootProjectMembers,
    projectMemberInvites,
    projectMembers,
    smartContracts,
    taskMemberInvites,
    taskMembers,
    team
  ]);

  const usersWithHighlight = useMemo<
    ProjectTeamMentionsPluginUserOption[]
  >(() => {
    const highlightIdsArray = [];

    if (taskNanoId) {
      highlightIdsArray.push(
        ...taskMemberInvites.map((invite) => invite.invitedUser.id),
        ...taskMembers.map((member) => member.user.id)
      );
    } else {
      highlightIdsArray.push(
        ...rootProjectMembers.map((member) => member.user.id),
        ...projectMembers.map((member) => member.user.id),
        ...projectMemberInvites.map((invite) => invite.invitedUser.id)
      );
    }

    const highlightedIdsSet = new Set(highlightIdsArray);

    return users.map((user) => ({
      ...user,
      highlight: highlightedIdsSet.has(user.id)
    }));
  }, [
    projectMemberInvites,
    projectMembers,
    rootProjectMembers,
    taskMemberInvites,
    taskMembers,
    taskNanoId,
    users
  ]);

  const selectEveryoneOptions = useMemo<
    ProjectTeamMentionsPluginUserOption[]
  >(() => {
    const allProjectMembers =
      size(projectMembers) > 0 && !taskNanoId
        ? {
            id: MentionsPluginAllValues.ALL_PROJECT_MEMBERS,
            fullName: 'All'
          }
        : null;

    const allProjectPerformers =
      size(projectMembers) > 0 && !taskNanoId
        ? {
            id: MentionsPluginAllValues.ALL_PROJECT_PERFORMERS,
            fullName: 'All performers'
          }
        : null;

    const allTaskMembers =
      size(taskMembers) > 0
        ? {
            id: MentionsPluginAllValues.ALL_TASK_MEMBERS,
            fullName: 'All'
          }
        : null;

    const allTaskPerformers =
      size(taskMembers) > 0
        ? {
            id: MentionsPluginAllValues.ALL_TASK_PERFORMERS,
            fullName: 'All performers'
          }
        : null;

    return compact([
      allProjectMembers,
      allProjectPerformers,
      allTaskMembers,
      allTaskPerformers
    ]);
  }, [projectMembers, taskMembers, taskNanoId]);

  const [fullName, setFullName] = useState<UserFullName>(null);

  const options = useMemo<MentionTypeaheadOption[]>(() => {
    const onlyHighlighted = filter(
      usersWithHighlight,
      (user) => !!user?.highlight
    );

    const usersWithAllOptions =
      !fullName && onlyHighlighted.length > 0
        ? [...selectEveryoneOptions, ...onlyHighlighted]
        : [...selectEveryoneOptions, ...usersWithHighlight];

    return filter(usersWithAllOptions, (user) =>
      includes(toLower(user.fullName), toLower(fullName))
    )?.map(
      (result) =>
        new MentionTypeaheadOption(
          result.id,
          result.fullName,
          result.image?.file,
          result.highlight
        )
    );
  }, [selectEveryoneOptions, usersWithHighlight, fullName]);

  const handleQueryChange = useCallback<(queryString: string) => void>(
    (queryString) => setFullName(queryString),
    []
  );

  return (
    <MentionsPlugin
      options={options}
      initialMentionIds={initialMentionIds}
      onQueryChange={handleQueryChange}
    />
  );
}

export default ProjectTeamMentionsPlugin;
